<template>
  <div class="definition-actions">
    <template v-if="showMockData">
      <v-btn
        dark
        :color="useMockData ? 'success' : 'light-blue lighten-1'"
        @click.native="toggleMockData"
        :disabled="viewOnly"
        id="toggleMockDataBtn"
      >
        Toggle Mock Data
      </v-btn>
    </template>
    <action-button
      v-if="showSendDownstream"
      dark
      :disabled="disableDownStreamButton || viewOnly"
      @click.native="submitDownstream"
      :label="sendDownstreamLabel"
      color="primary"
      id="sendDownstreamBtn"
    />
    <action-button
      @click.native="downloadTargetPackage"
      class="definition-actions__clear-btn"
      v-if="lasActions && allowDownloadBtn"
      label="download"
      id="downloadBtn"
      color="secondary"
    />
    <action-button
      v-if="showGenerateEstimate"
      dark
      :disabled="disableLASDownStreamButton || viewOnly"
      class="definition-actions__clear-btn"
      @click.native="submitDownstream"
      :label="activateLabel"
      id="generateEstimateBtn"
      color="primary"
    />
    <action-button
      dark
      :disabled="canNotUndo || viewOnly"
      @click.native="undo"
      label="undo"
      id="undoBtn"
      color="secondary"
    />
    <action-button
      :disabled="canNotRedo || viewOnly"
      @click.native="redo"
      dark
      label="redo"
      id="redoBtn"
      color="secondary"
    />
    <action-button
      :disabled="isClearAllDisabled || viewOnly"
      @click.native="clear"
      dark
      label="Clear All"
      id="clearAllBtn"
      color="secondary"
    />
    <action-button
      :disabled="canNotCancel || viewOnly"
      color="secondary"
      @click.native="cancel"
      dark
      label="cancel"
      id="cancelBtn"
    />
    <v-tooltip
      v-model="hover"
      top
      min-width="300px"
      :disabled="disableToolTip"
    >
      <template v-slot:activator="{ on }">
        <div v-on="on">
          <v-btn
            :disabled="disableSave || viewOnly"
            @click.native="save"
            dark
            color="success"
            style="min-width: 100px"
            v-on="on"
            id="saveBtn"
          >
            save
          </v-btn>
        </div>
      </template>
      <span>{{ saveButtonTitle }}</span>
    </v-tooltip>
    <send-downstream
      ref="sendDownstreamModal"
      :value="targetData"
    />
  </div>
</template>

<script>
  import ActionButton from '@/components/common/actionButton'
  import Toast from '@/components/common/toasts'
  import SendDownstream from '@/appComponents/workbench/sendDownstream/SendDownstream.vue'
  import { CLEAR_DEFINITION } from '@/appComponents/workbench/targetDefinition/store/actions'
  import { SAVE_TARGET, RESET_FAMILY_DATASETS } from '@/appLogic/workbench/common/store/actions'
  // TODO need to create a seperate slotted DefinitionActions component
  import { TOGGLE_MOCK_DATA, SET_ENABLE_UPDATE_INSIGHTS } from '@/appLogic/workbench/Xandr/store/actions'
  import { targetRouteHandler, definitionAsHtml } from '@/helpers'
  import { DOWNLOAD_TARGET_DEFINITION } from '@/appLogic/targets/currentTarget/store/actions'
  import { TARGET_TYPE, ACTIVATION_STATUS, DESTINATIONS } from '@/appLogic/targets/Constants'
  // TODO: create a wrapper to handle getting the chart data for download

  export default {
    props: {
      allowDownloadBtn: {
        type: Boolean,
      },
      targetData: {
        type: Object,
      },
      lasActions: {
        type: Boolean,
      },
      xandrActions: {
        type: Boolean,
      },
      viewOnly: {
        type: Boolean,
      },
    },
    data() {
      return {
        useMockData: this.$store.getters['workbench/xandrUseMockData'],
        hover: false,
      }
    },
    components: {
      ActionButton,
      SendDownstream,
    },
    mounted() {
      const unWatch = this.$watch(
        // watch only once until newWorkbenchTarget is available
        () => this.$store.getters['workbench/newWorkbenchTarget'],
        wbTarget => {
          if (wbTarget) {
            if (this.isDraft) {
              this.useMockData = this.$store.getters['workbench/xandrUseMockData']
            } else if (
              wbTarget.xandr_data &&
              wbTarget.xandr_data.query &&
              wbTarget.xandr_data.query.counts.isUsingMockData
            ) {
              // Existing Insights is using mock data
              this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA, true)
              this.useMockData = wbTarget.xandr_data.query.counts.isUsingMockData
            } else {
              this.useMockData = false
              if (this.isUsingMockData) {
                this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA, false)
              }
            }
            unWatch()
          }
        },
      )
    },
    computed: {
      has12monthsOfData() {
        const workbenchData = this.targetData && this.targetData.workbench_data
        const universe = workbenchData.universe && workbenchData.universe.universe
        return universe.length >= 12
      },
      activateLabel() {
        return this.currentTarget && this.currentTarget.target === TARGET_TYPE.SD ? 'Activate' : 'Send Downstream'
      },
      sendDownstreamLabel() {
        return this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.FAILED
          ? 'Retry Send Downstream'
          : 'Send Downstream'
      },
      enableDownStream() {
        let eds = this.$store.state.workbench.enableDownstream
        return (
          (eds === 'undefined' ? true : eds) &&
          this.targetData &&
          this.targetData.target_definition &&
          this.targetData.target_definition.query &&
          this.targetData.target_definition.query.length > 0
        )
        // question: for working status, when click start editing, do we start w/ enable Send D? here assume not
        // return this.$store.state.workbench.enableDownstream || this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.WORKING
      },
      enableSaving() {
        return this.$store.state.workbench.enableSaving
      },
      isClearAllDisabled() {
        return this.enableDefinitionActions ? !this.$store.getters['workbench/hasDefinition'] : true
      },
      fetchingTarget() {
        return this.$store.getters['workbench/isFetchingTarget']
      },
      loading() {
        return this.$store.getters['workbench/loading']
      },
      enableDefinitionActions() {
        return this.$store.getters['workbench/enableDefinitionActions']
      },
      canNotUndo() {
        return this.enableDefinitionActions ? this.$store.getters['workbench/canNotUndo'] : true
      },
      canNotRedo() {
        return this.enableDefinitionActions ? this.$store.getters['workbench/canNotRedo'] : true
      },
      canNotCancel() {
        if (
          (this.targetData &&
            this.targetData.activation_status &&
            this.targetData.activation_status === ACTIVATION_STATUS.DRAFT) ||
          (this.targetData &&
            this.targetData.activation_status &&
            this.targetData.activation_status === ACTIVATION_STATUS.NEW)
        ) {
          return false
        } else if (
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.WORKING) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.ASSIGNED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.ACTIVATED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.POSTED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.SUBMITTED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.FAILED) ||
          (this.targetData && this.targetData.activation_status === ACTIVATION_STATUS.LIVE) ||
          this.fetchingTarget ||
          this.loading
        ) {
          return true
        }
      },
      currentTarget() {
        return this.$store.getters['workbench/currentTarget']
      },
      disableDownStreamButton() {
        return !this.enableDownStream || this.isDraft || !this.$store.getters.hasPublishPermissions
      },
      disableLASDownStreamButton() {
        return !this.$store.state.workbench.enableDownstream || !this.$store.getters.hasPublishPermissions
      },
      showGenerateEstimate() {
        return this.lasActions
      },
      isUsingMockData() {
        return this.$store.getters['workbench/xandrUseMockData']
      },
      as() {
        return (this.$store.getters['workbench/newWorkbenchTarget'] || {}).activation_status
      },
      isWorking() {
        return this.as === ACTIVATION_STATUS.WORKING
      },
      isDraft() {
        return this.as === ACTIVATION_STATUS.DRAFT
      },
      isLive() {
        return this.as === ACTIVATION_STATUS.LIVE
      },
      isModeled() {
        return this.as === ACTIVATION_STATUS.MODELED
      },
      isModeling() {
        return this.as === ACTIVATION_STATUS.MODELING
      },
      isFailed() {
        return this.as === ACTIVATION_STATUS.FAILED
      },
      showMockData() {
        return this.xandrActions && process.env.ENABLE_XANDR_MOCK_DATA_BUTTON
      },
      showSendDownstream() {
        return this.xandrActions
      },
      disableSave() {
        return !this.enableSaving
      },
      saveButtonTitle() {
        return this.xandrActions && !this.enableDownStream
          ? 'Please click the Update Insights button before saving'
          : null
      },
      disableToolTip() {
        return !this.saveButtonTitle || !this.disableSave
      },
    },
    methods: {
      clear() {
        this.$store.dispatch('workbench/' + CLEAR_DEFINITION)
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      generateEstimate() {
        targetRouteHandler('ACTIVATE', this.currentTarget)
      },
      save() {
        const user = this.$store.getters['workbench/username']
        let status
        if (
          this.targetData.activation_status === ACTIVATION_STATUS.DRAFT ||
          this.targetData.activation_status === ACTIVATION_STATUS.ACTIVATED
        ) {
          status = ACTIVATION_STATUS.WORKING
        } else {
          status = this.targetData.activation_status
        }
        const target = { ...this.targetData, username: user, activation_status: status }
        this.$store.dispatch('workbench/' + SAVE_TARGET, { target })
      },
      undo() {
        this.$store.dispatch('workbench/undoHistory/UNDO')
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      redo() {
        this.$store.dispatch('workbench/undoHistory/REDO')
        this.$store.dispatch('workbench/' + RESET_FAMILY_DATASETS)
      },
      cancel() {
        if (!this.canNotCancel) {
          this.$router.go(-1)
        }
      },
      downloadTargetPackage() {
        this.$store.dispatch(DOWNLOAD_TARGET_DEFINITION, {
          targetId: this.currentTarget.id,
        })
      },
      toggleMockData() {
        this.$store.dispatch('workbench/' + TOGGLE_MOCK_DATA)
        this.$store.dispatch('workbench/' + SET_ENABLE_UPDATE_INSIGHTS, true)
        this.useMockData = !this.useMockData
      },
      submitDownstream() {
        if (this.lasActions && !this.has12monthsOfData) {
          Toast.error({
            title: 'Validation Error',
            message: 'Target must have at least 12 months of data, please update the target and try again.',
            timeout: 5000,
          })
        } else this.$refs.sendDownstreamModal.open()
      },
    },
    // watch: {
    //   isUsingMockData () {
    //     this.useMockData = this.$store.getters['workbench/xandrUseMockData']
    //   }
    // }
  }
</script>

<style lang="scss">
  .definition-actions {
    &__save-btn,
    &__clear-btn {
      min-width: rem(140);
    }
    .enabled {
      pointer-events: all !important;
    }
  }
</style>



// WEBPACK FOOTER //
// DefinitionActions.vue?5c7a4abc